import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import type { CustomerPrivacyKind } from '../api/customers-api';
import { useOpenCustomerPrivacyRequest } from '../hooks/use-customer-mutations';

const KINDS: readonly CustomerPrivacyKind[] = ['export', 'delete'];

export interface CustomerPrivacyRequestDialogProps {
  storeId: string;
  customer: Customer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Records an export or deletion the shopper asked for by phone or email. Nothing runs yet: staff
 * complete it from the privacy requests list like a request filed on the storefront.
 */
export function CustomerPrivacyRequestDialog({
  storeId,
  customer,
  open,
  onOpenChange,
}: CustomerPrivacyRequestDialogProps) {
  const { t } = useTranslation(['customers', 'common']);
  const message = useErrorMessage();
  const create = useOpenCustomerPrivacyRequest(storeId, customer.id);
  const [kind, setKind] = useState<CustomerPrivacyKind>('export');
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    onOpenChange(false);
    create.reset();
  };

  const submit = async () => {
    setError(null);
    try {
      await create.mutateAsync(kind);
      close();
    } catch (failure) {
      const alreadyOpen = failure instanceof ApiError && failure.code === 'ALREADY_EXISTS';
      setError(
        alreadyOpen
          ? t('customers:privacyRequest.alreadyOpen', {
              kind: t(`customers:privacyRequest.kinds.${kind}.label`),
            })
          : message(failure),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('customers:privacyRequest.title')}</DialogTitle>
          <DialogDescription>
            {t('customers:privacyRequest.description', { email: customer.email })}
          </DialogDescription>
        </DialogHeader>
        <RadioGroup
          value={kind}
          onValueChange={(value) => {
            setKind(value as CustomerPrivacyKind);
            setError(null);
          }}
          aria-label={t('customers:privacyRequest.kind')}
          className="gap-2"
        >
          {KINDS.map((option) => (
            <Label
              key={option}
              htmlFor={`privacy-kind-${option}`}
              className="flex cursor-pointer items-start gap-2 rounded-md border p-3 font-normal"
            >
              <RadioGroupItem id={`privacy-kind-${option}`} value={option} className="mt-0.5" />
              <span className="grid gap-0.5 text-sm">
                <span className="font-medium">
                  {t(`customers:privacyRequest.kinds.${option}.label`)}
                </span>
                <span className="text-muted-foreground text-xs">
                  {t(`customers:privacyRequest.kinds.${option}.hint`)}
                </span>
              </span>
            </Label>
          ))}
        </RadioGroup>
        {error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" disabled={create.isPending} onClick={() => void submit()}>
            {t('customers:privacyRequest.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
